/**
 * Trigonometry Peaks, surveyed: the mountain, and the inside of all three of
 * its regions (docs/rpg.md §22, `.claude/skills/world-maps/SKILL.md`).
 *
 * **The climb is the mathematics.** Height on this map is how general the angle
 * is allowed to be. Down at the camp a right triangle has three named sides and
 * the angle has to be acute, because there is no room in a right triangle for
 * anything else. At the summit the angle stops being a corner and becomes a
 * journey round a wheel, which is what lets it be any angle at all. And the
 * ridge running east off the summit holds what is true for *every* angle at
 * once. So there is one way up, it is a switchback, and a student walks the
 * generality in the order the mathematics earns it.
 *
 * The one thing a mountain kingdom must be careful about: `^` refuses a step,
 * so this is the easiest map in the codebase to fence a region off by accident
 * — and a fenced-off region is indistinguishable, from inside the game, from
 * being told you are not good enough for it. `validateTown` and
 * `validateRegionMap` walk all four of these from their arrival tiles on every
 * `npm run content:validate`. Every region here is also an ordinary card on the
 * kingdom page, so the map is the picture and the cards are the index.
 */

import type { RegionMap, Town } from '@yuan-math/world';

const PEAKS = 'kingdom-trigonometry';

/* ========================================================== the mountain === */

/**
 * The mountain: forest at the foot, the camp shelf, the switchback, the crest.
 *
 * The road in from the world arrives on the west edge at the level of the camp,
 * because you reach a mountain before you climb it. From the camp there is one
 * pull north onto the first ledge, one back east, one west again, and then the
 * high road along the top — three legs, which is what a switchback is for and
 * why nobody walks up a mountain in a straight line. The cairn at the far east
 * end of the ridge and the peak above the shelf are both spurs, so a student
 * walking the crest is never carried into either by accident.
 */
const TRIGONOMETRY_PEAKS: Town = {
  kingdomId: PEAKS,
  caption:
    'One way up: the camp on the shelf, the switchback through the crags, the wheel on the summit and the ridge running east off it.',
  width: 26,
  height: 18,
  /* On the camp shelf, a couple of steps west of the notice board and in sight
     of the pull that starts the climb. Not on a signpost: arriving somewhere is
     going there, so a student put down on one would be sent straight back in. */
  start: { x: 4, y: 9 },
  rows: [
    '^^^^^^^^^^^^^^^^^^^^^^^^^^',
    '^^^^^^^^^^^.^^^^^^^^^^^^^^',
    '^^^.............^^^^^^^^^^',
    '^^^^##################^^^^',
    '^^^^^^^#^^^^^^^^^^^^^#^^^^',
    '^^^^^^^###########^^^#^^^^',
    '^^^^^^^^^^^^^^^^^#^^^^^^^^',
    '^^^^^^^^##########^^^^^^^^',
    '^^^^^^^^#^^^^^^^^^^^^^^^^^',
    '^.......#..^^^^^^^^^^^^^^^',
    '^########..^^^^^^^^^^^^^^^',
    '^..................~~~~..^',
    '^.ffff............ffffff.^',
    '^.ffff............ffffff.^',
    '^ffffffffffffffffffffffff^',
    '^ffffffffffffffffffffffff^',
    '^ffffffffffffffffffffffff^',
    '^^^^^^^^^^^^^^^^^^^^^^^^^^',
  ],
  /**
   * Six places, in the order a climber meets them: the road back down, the
   * board, the camp at the foot of the pull, then the summit, the far cairn and
   * the peak. The two that name nothing in the graph carry their own words,
   * because no concept is called a notice board.
   */
  places: [
    { kind: 'road', name: 'The Road Down', emoji: '🗺️', x: 1, y: 10 },
    { kind: 'quests', name: 'The Notice Board', emoji: '📜', x: 3, y: 9 },
    { kind: 'region', regionId: 'region-ratio-basecamp', x: 10, y: 9 },
    { kind: 'region', regionId: 'region-unit-circle-summit', x: 6, y: 2 },
    { kind: 'region', regionId: 'region-identity-ridge', x: 21, y: 5 },
    { kind: 'boss', bossId: 'boss-trigonometry-kingdom', x: 11, y: 1 },
  ],
};

/* =========================================================== the regions === */

/**
 * Ratio Basecamp — two huts on a shelf under the crag.
 *
 * The naming hut on the west and the surveying shed on the east, which is the
 * whole of what this region is: first the three sides get names, then you go
 * out and measure something with them. The trail down is the way back to the
 * camp shelf you came from.
 */
const RATIO_BASECAMP: RegionMap = {
  kingdomId: PEAKS,
  regionId: 'region-ratio-basecamp',
  caption: 'Scrub under the crag, the hut where the three sides get their names, and the shed where you go and measure something with them.',
  width: 17,
  height: 11,
  /* Out on the open shelf between the two huts, with both in sight. */
  start: { x: 7, y: 7 },
  rows: [
    '^^^^^^^^^^^^^^^^^',
    '^,,,,,,,,,,,,,,,^',
    '^..%%%%..%%%%%..^',
    '^..%__%..%___%..^',
    '^..%_%%..%%_%%..^',
    '^...............^',
    '^.^^............^',
    '^............^^.^',
    '^...............^',
    '^......#........^',
    '^^^^^^^^^^^^^^^^^',
  ],
  places: [
    { kind: 'concept', conceptId: 'trig-ratios', x: 4, y: 3 },
    { kind: 'concept', conceptId: 'trig-applications', x: 11, y: 3 },
    { kind: 'road', name: 'The Trail Down', emoji: '⛰️', x: 7, y: 9 },
  ],
};

/**
 * Unit Circle Summit — a ring of standing stones, and the terrace beside it.
 *
 * The ring has a gate north and a gate south, because a circle you cannot walk
 * into is a wall. The wheel itself is at the centre; the tangent stands just
 * outside the ring, touching it, which is what a tangent does; and the graphs
 * are out on the long straight terrace to the east, where the circle has been
 * unrolled into a line. Nothing here explains any of that — a place is a
 * signpost — but the shapes are the right shapes.
 */
const UNIT_CIRCLE_SUMMIT: RegionMap = {
  kingdomId: PEAKS,
  regionId: 'region-unit-circle-summit',
  caption: 'A ring of standing stones with a gate north and south, the stone that touches it, and the long terrace where the circle unrolls into a line.',
  width: 18,
  height: 13,
  /* Just inside the south gate, looking up the ring. */
  start: { x: 7, y: 10 },
  rows: [
    '^^^^^^^^^^^^^^^^^^',
    '^................^',
    '^................^',
    '^...%%...%%..#...^',
    '^...%.....%..#...^',
    '^..%.......%.#...^',
    '^...%.....%..#...^',
    '^...%%...%%..#...^',
    '^............#...^',
    '^................^',
    '^................^',
    '^......#.........^',
    '^^^^^^^^^^^^^^^^^^',
  ],
  places: [
    { kind: 'concept', conceptId: 'unit-circle', x: 7, y: 5 },
    { kind: 'concept', conceptId: 'tangent-function', x: 12, y: 5 },
    { kind: 'concept', conceptId: 'trig-graphs', x: 13, y: 8 },
    { kind: 'road', name: 'The Way Down', emoji: '⛰️', x: 7, y: 11 },
  ],
};

/**
 * Identity Ridge — three cairns on the crest, two hollows under it.
 *
 * The crest carries what is true for every angle: the identities, the addition
 * formulas built on them, and the half-angle work built on those, in that order
 * walking east. The two hollows below the path are where you come down off the
 * general statements to do something with them — solve an equation, or take a
 * triangle that has no right angle in it at all.
 */
const IDENTITY_RIDGE: RegionMap = {
  kingdomId: PEAKS,
  regionId: 'region-identity-ridge',
  caption: 'A spine of rock with three cairns along the top, and two hollows under the path where the general statements get used.',
  width: 19,
  height: 10,
  /* On the spine, midway, with a cairn either side. */
  start: { x: 8, y: 4 },
  rows: [
    '^^^^^^^^^^^^^^^^^^^',
    '^^^#^^^^^#^^^^^#^^^',
    '^^.#.^^^.#.^^^.#.^^',
    '^..#.....#.....#..^',
    '^#################^',
    '^^^^^^#^^^^^#^^^^#^',
    '^^^^^.#.^^^.#.^^^^^',
    '^^^^^.#.^^^.#.^^^^^',
    '^^^^^^^^^^^^^^^^^^^',
    '^^^^^^^^^^^^^^^^^^^',
  ],
  places: [
    { kind: 'concept', conceptId: 'trig-identities', x: 3, y: 1 },
    { kind: 'concept', conceptId: 'trig-addition-formulas', x: 9, y: 1 },
    { kind: 'concept', conceptId: 'half-angle-formulas', x: 15, y: 1 },
    { kind: 'concept', conceptId: 'trig-equations', x: 6, y: 7 },
    { kind: 'concept', conceptId: 'law-of-sines-cosines', x: 12, y: 7 },
    { kind: 'road', name: 'The Ridge End', emoji: '⛰️', x: 17, y: 5 },
  ],
};

/* -------------------------------------------------------------------------- */

export const TRIGONOMETRY_TOWN: Town = TRIGONOMETRY_PEAKS;

export const TRIGONOMETRY_REGION_MAPS: readonly RegionMap[] = [
  RATIO_BASECAMP,
  UNIT_CIRCLE_SUMMIT,
  IDENTITY_RIDGE,
];
